<template>
  <spinner
    v-if="loading"
    sizes="w-20 h-20"
    thickness="border-8" />
  <RefonteCard
    v-else-if="formateurClientDocuments && formateurClientDocuments.length > 0">
    <div class="mb-8">
      <document-upload-modal
        v-if="selectedDocument && selectedClientId"
        v-bind:show="isUploadModalOpen"
        v-bind:client_id="selectedClientId"
        v-bind:document="selectedDocument"
        v-bind:on_submit="fetchDocuments"
        v-bind:show_engagement="selectedDocument.type == 2"
        v-bind:url_prefix="props.url_prefix"
        v-bind:on_close="closeUploadModalOpen" />
      <template
        v-for="formateurClientDocument in formateurClientDocuments"
        v-bind:key="formateurClientDocument.clientId">
        <div class="mb-3 mt-6 flex items-center justify-between">
          <div
            class="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-teal-700">
            <div class="icon"><IconBills /></div>
            <h3>
              {{ formateurClientDocument.denomination }}
            </h3>
          </div>
          <div class="flex items-center gap-2">
            <span
              v-if="isDossierComplet(formateurClientDocument.documents)"
              class="bg-teal-50 flex items-center rounded-full border border-jinius-green px-2.5 py-1 text-xs font-medium text-jinius-green">
              <IconValidate20Green />
              Dossier complet
            </span>
            <span
              v-else
              class="bg-orange-50 flex items-center rounded-full border border-orange-200 px-2.5 py-1 text-xs font-medium text-orange-700">
              <IconWarning20Orange />
              Dossier incomplet
            </span>
          </div>
        </div>
        <div
          v-bind:class="`border-gray-200 overflow-hidden rounded-lg border border-l-4 bg-white shadow-sm transition-colors ${isDossierComplet(formateurClientDocument.documents) ? 'border-l-teal-600' : 'border-l-orange-600'}`">
          <table class="w-full border-separate border-spacing-0 text-left">
            <thead>
              <tr
                class="bg-[#2C3E50] text-xs uppercase tracking-wider text-white">
                <th class="px-4 py-3 text-left font-semibold">
                  Document requis
                </th>
                <th class="w-96 px-4 py-3 text-center font-semibold">Statut</th>
                <th class="w-32 px-4 py-3 text-center font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="document in formateurClientDocument.documents"
                v-bind:key="document.type"
                class="border-gray-100 hover:bg-gray-50/50 border-b transition-colors last:border-0">
                <td class="px-4 py-4 text-sm">
                  <div class="text-gray-800 font-bold">
                    {{ document.name }}
                  </div>
                  <div
                    class="text-gray-500 mt-1.5 text-xs"
                    v-if="document.lastUpdatedDate">
                    Mis à jour le {{ document.lastUpdatedDate }}
                  </div>
                </td>
                <td class="px-4 py-4 text-center">
                  <div class="flex flex-col items-center gap-1">
                    <span
                      v-if="document.expired"
                      class="inline-flex items-center text-sm font-bold text-danger-500">
                      <IconFailed20Red />
                      Expiré
                    </span>
                    <span
                      v-else-if="document.id"
                      class="inline-flex items-center text-sm font-bold text-jinius-green">
                      <IconValidate20Green />
                      Transmis
                    </span>
                    <span
                      v-else-if="document.required"
                      class="inline-flex items-center text-sm font-bold text-orange-600">
                      <IconWarning20Orange />
                      À fournir
                    </span>
                    <span
                      class="text-gray-400 text-sm italic"
                      v-else>
                      Optionnel
                    </span>
                    <span
                      v-if="document.expirationDate"
                      v-bind:class="`${document.expired ? 'border-danger-500 text-danger-500' : 'text-gray-500 border-gray-200'} mt-1 rounded-full border bg-white px-2 py-0.5 text-[11px] shadow-sm`">
                      {{ document.expired ? 'Expiré le' : 'Expire le ' }}
                      {{ document.expirationDate }}
                    </span>
                  </div>
                </td>
                <td class="px-4 py-4 text-center">
                  <div class="flex items-center justify-center gap-3">
                    <refonte-button
                      v-if="!document.id"
                      variant="secondary"
                      v-bind:no_label="true"
                      v-bind:icon_left="IconUpload"
                      v-tooltip="'Téléverser le document'"
                      v-on:click="
                        () =>
                          openUploadModalOpen(
                            document,
                            formateurClientDocument.clientId
                          )
                      " />
                    <template v-else>
                      <refonte-button
                        variant="secondary"
                        v-bind:no_label="true"
                        v-bind:icon_left="IconDownload"
                        v-tooltip="'Télécharger le document'"
                        v-on:click="() => downloadFile(document.id)" />

                      <refonte-button
                        variant="danger"
                        v-bind:no_label="true"
                        v-bind:icon_left="IconClose"
                        v-tooltip="'Supprimer le document'"
                        v-on:click="() => deleteClientDocument(document.id)" />
                    </template>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </RefonteCard>
  <refonte-page-alert
    v-else
    title="Aucun justificatif requis"
    v-bind:description_in_card="true"
    v-bind:alert_type="'news'">
    Les tiers sélectionnés ne nécessitent pas d'action de votre part concernant
    les documents. Sélectionnez un autre tiers.
  </refonte-page-alert>
</template>
<script setup lang="ts">
  import { onMounted, ref, watch } from 'vue';
  import IconDownload from '@/assets/img/refonte/icons/display/icon-download.svg?component';
  import IconUpload from '@/assets/img/refonte/icons/display/icon-upload.svg?component';
  import RefonteCard from '@/components/refonte/cards/refonte-card.vue';
  import { API } from '@/http-common';
  import {
    ClientDocument,
    FormateurClientDocument
  } from '@/types/formateur/client-documents';
  import DocumentUploadModal from '@/components/refonte/documents/document-upload-modal.vue';
  import http_functions from '@/mixin/http_functions';
  import Spinner from '@/components/utils/spinner.vue';
  import RefonteButton from '@/components/refonte/buttons/refonte-button.vue';
  import IconClose from '@/assets/img/refonte/icons/display/icon-close.svg?component';
  import IconBills from '@/assets/img/refonte/icons/menu/icon-bills.svg?component';
  import IconWarning20Orange from '@/assets/img/refonte/icons/information/icon-warning-sign-20-orange.svg?component';
  import IconValidate20Green from '@/assets/img/refonte/icons/information/icon-validate-20-green.svg?component';
  import IconFailed20Red from '@/assets/img/refonte/icons/information/icon-failed-20-red.svg?component';
  import RefontePageAlert from '@/components/refonte/alerts/refonte-page-alert.vue';

  const props = withDefaults(
    defineProps<{
      url_prefix: string;
      client_ids?: number[];
    }>(),
    {}
  );
  const formateurClientDocuments = ref<FormateurClientDocument[]>([]);

  watch(
    () => props.client_ids,
    () => {
      fetchDocuments();
    }
  );

  onMounted(async () => {
    await fetchDocuments();
  });

  const fetchDocuments = async () => {
    loading.value = true;
    const response = await API.get(
      `${props.url_prefix}/client-documents${props.client_ids && props.client_ids.length > 0 ? `?clientIds=${props.client_ids.join(',')}` : ''}`
    );

    if (response && response.data) {
      formateurClientDocuments.value = response.data;
    }
    loading.value = false;
  };

  const isUploadModalOpen = ref<boolean>(false);
  const selectedDocument = ref<ClientDocument>();
  const selectedClientId = ref<number>();
  const loading = ref<boolean>();

  function closeUploadModalOpen() {
    isUploadModalOpen.value = false;
    selectedDocument.value = undefined;
    selectedClientId.value = undefined;
  }
  const openUploadModalOpen = async (row: ClientDocument, clientId: number) => {
    isUploadModalOpen.value = true;
    selectedClientId.value = clientId;
    selectedDocument.value = row;
  };

  // Règle de gestion : Le dossier est complet si tous les documents OBLIGATOIRES sont transmis
  const isDossierComplet = (documents: ClientDocument[]) => {
    return documents
      .filter((doc) => doc.required)
      .every((doc) => doc.id != undefined && !doc.expired);
  };

  const downloadFile = (clientDocumentId: number | undefined) => {
    if (clientDocumentId) {
      http_functions.methods.downloadFile(
        `${props.url_prefix}/client-documents/${clientDocumentId}/file`
      );
    }
  };

  const deleteClientDocument = async (clientDocumentId: number | undefined) => {
    if (clientDocumentId) {
      await API.delete(
        `${props.url_prefix}/client-documents/${clientDocumentId}`,
        {
          headers: {
            'SKIP-REDIRECTION': true
          }
        }
      );
      await fetchDocuments();
    }
  };
</script>

<style lang="scss" scoped>
  .document-action-button {
    :deep(svg) {
      min-width: 22px;
      width: 22px;
      height: 22px;
    }
  }
  .icon {
    :deep(svg) {
      min-width: 16px;
      width: 16px;
      height: 16px;
    }
  }
</style>
